import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { marked } from 'marked';
import { reviewAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { capitalize, linkifyText } from '@/lib/utils';
import { ArrowLeft, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { CommentSection } from '../components/CommentSection';
import FeedbackButtons from '../components/FeedbackButtons';
import { ResponseVersionCarousel } from '../components/ResponseVersionCarousel';

export default function EmailDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editedResponse, setEditedResponse] = useState('');
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [showFullEmail, setShowFullEmail] = useState(false);

  // Function to extract key parts from email body
  const parseEmailBody = (body: string) => {
    const orderIdMatch = body.match(/#\s*\d+-\d+-\d+/);
    const orderId = orderIdMatch ? orderIdMatch[0] : null;
    
    // Extract product line (line with ASIN)
    const productMatch = body.match(/\d+\s*\/\s*[^\n]+\[ASIN:[^\]]+\]/);
    const productLine = productMatch ? productMatch[0] : null;
    
    // Extract message between delimiters
    const messageMatch = body.match(/-------------\s*Nachricht:\s*-------------\s*([\s\S]*?)\s*-------------\s*Ende der Nachricht\s*-------------/);
    const customerMessage = messageMatch ? messageMatch[1].trim() : null;
    
    return { orderId, productLine, customerMessage, fullBody: body };
  };

  const { data: email, isLoading, refetch } = useQuery({
    queryKey: ['email', id],
    queryFn: async () => {
      const response = await reviewAPI.getById(id!);
      return response.data;
    },
    enabled: !!id,
  });

  // Auto-expand for eBay messages (they have full conversation context)
  useEffect(() => {
    if (email) {
      // Check if it's an eBay message by platform or subject line
      const isEbay = email.platform === 'ebay' || 
                     email.subject?.includes('Rückgabe') ||
                     email.subject?.includes('eBay') ||
                     email.subject?.includes('hat eine Nachricht gesendet');
      
      if (isEbay) {
        setShowFullEmail(true);
      }
    }
  }, [email]);

  const { data: orderData } = useQuery({
    queryKey: ['order-data', id],
    queryFn: async () => {
      const response = await reviewAPI.getOrderData(id!);
      return response.data;
    },
    enabled: !!id && orderDialogOpen,
  });

  const { data: responseVersions } = useQuery({
    queryKey: ['response-versions', id],
    queryFn: async () => {
      const response = await reviewAPI.getResponseVersions(id!);
      return response.data;
    },
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (response: string) => reviewAPI.updateResponse(id!, response),
    onSuccess: () => {
      toast.success('Response updated');
      setIsEditing(false);
      refetch();
    },
  });

  const approveMutation = useMutation({
    mutationFn: () => reviewAPI.approve(id!, 'admin'),
    onSuccess: () => {
      toast.success('Email approved');
      const currentParams = new URLSearchParams(window.location.search);
      const returnUrl = currentParams.toString() ? `/review?${currentParams.toString()}` : '/review';
      navigate(returnUrl);
    },
  });

  const approveAndSendMutation = useMutation({
    mutationFn: () => reviewAPI.approveAndSend(id!, 'admin'),
    onSuccess: () => {
      toast.success('Email approved and sent successfully');
      const currentParams = new URLSearchParams(window.location.search);
      const returnUrl = currentParams.toString() ? `/review?${currentParams.toString()}` : '/review';
      navigate(returnUrl);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => reviewAPI.reject(id!, 'admin'),
    onSuccess: () => {
      toast.success('Email rejected');
      const currentParams = new URLSearchParams(window.location.search);
      const returnUrl = currentParams.toString() ? `/review?${currentParams.toString()}` : '/review';
      navigate(returnUrl);
    },
  });

  const reprocessMutation = useMutation({
    mutationFn: () => reviewAPI.reprocess(id!),
    onSuccess: () => {
      toast.success('Email moved back to review queue');
      refetch();
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: () => reviewAPI.regenerate(id!),
    onSuccess: () => {
      toast.success('Response regenerated successfully');
      refetch();
    },
  });

  const sendMutation = useMutation({
    mutationFn: () => reviewAPI.send(id!),
    onSuccess: () => {
      toast.success('Email sent successfully');
      refetch();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!email) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">Email not found</p>
        </CardContent>
      </Card>
    );
  }

  const handleSaveEdit = () => {
    updateMutation.mutate(editedResponse);
  };

  const handleStartEdit = () => {
    setEditedResponse(email.edited_response || email.response_text || '');
    setIsEditing(true);
  };

  return (
    <div className="space-y-6 w-full px-4 pb-8">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => {
          const currentParams = new URLSearchParams(window.location.search);
          const returnUrl = currentParams.toString() ? `/review?${currentParams.toString()}` : '/review';
          navigate(returnUrl);
        }}
        size="sm"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Review Queue
      </Button>

      {/* Email Header */}
      <Card>
        <CardHeader className="pb-4">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-xl sm:text-2xl mb-2 break-words">{email.subject}</CardTitle>
                <CardDescription className="text-sm sm:text-base break-words">
                  From: {email.from_address.split('<')[0].trim() || email.from_address}
                </CardDescription>
              </div>
              <div className="flex gap-2 flex-shrink-0 flex-wrap">
                {email.extracted_entities?.order_id && (
                  <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-blue-200 text-blue-700 hover:bg-blue-50"
                      >
                        <Package className="h-4 w-4 mr-2" />
                        Order Details
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Order Details</DialogTitle>
                      </DialogHeader>
                      {orderData?.available ? (
                        <div className="space-y-4">
                          {/* Order Info */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Order ID</p>
                              <p className="text-sm font-semibold">{orderData.data.order_id}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Status</p>
                              <Badge variant="outline">{orderData.data.order_status}</Badge>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Buyer</p>
                              <p className="text-sm">{orderData.data.buyer_name}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">Purchase Date</p>
                              <p className="text-sm">{new Date(orderData.data.purchase_date).toLocaleDateString()}</p>
                            </div>
                          </div>

                          {/* Product Info */}
                          {orderData.data.product && (
                            <div className="border-t pt-4">
                              <p className="text-sm font-semibold mb-3">Product Information</p>
                              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                                <p className="text-sm font-medium">{orderData.data.product.name}</p>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  <div><span className="text-muted-foreground font-medium">SKU:</span> {orderData.data.product.sku}</div>
                                  <div><span className="text-muted-foreground font-medium">ASIN:</span> {orderData.data.product.asin}</div>
                                  <div><span className="text-muted-foreground font-medium">Quantity:</span> {orderData.data.product.quantity}</div>
                                  {orderData.data.product.manufacturer && (
                                    <div><span className="text-muted-foreground font-medium">Manufacturer:</span> {orderData.data.product.manufacturer}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Tracking Info */}
                          {orderData.data.tracking && (
                            <div className="border-t pt-4">
                              <p className="text-sm font-semibold mb-3">Tracking Information</p>
                              <div className="bg-muted/30 rounded-lg p-4 space-y-2 text-sm">
                                <div><span className="text-muted-foreground font-medium">Tracking Number:</span> {orderData.data.tracking.number}</div>
                                <div><span className="text-muted-foreground font-medium">Shipped Date:</span> {new Date(orderData.data.tracking.shipped_date).toLocaleDateString()}</div>
                              </div>
                            </div>
                          )}

                          {/* Specs */}
                          {orderData.data.specs && Object.keys(orderData.data.specs).length > 0 && (
                            <div className="border-t pt-4">
                              <p className="text-sm font-semibold mb-3">Product Specifications</p>
                              <div className="bg-muted/30 rounded-lg p-4 grid grid-cols-2 gap-3 text-sm">
                                {Object.entries(orderData.data.specs).map(([key, value]) => (
                                  <div key={key}>
                                    <span className="text-muted-foreground font-medium">{key}:</span> {value as string}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="py-8 text-center">
                          <p className="text-sm text-muted-foreground">{orderData?.message || 'Loading order details...'}</p>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                )}
                {email.response_status === 'pending_review' && (
                  <>
                    <Button
                      onClick={() => regenerateMutation.mutate()}
                      disabled={regenerateMutation.isPending}
                      variant="outline"
                      size="sm"
                      className="border-purple-200 text-purple-700 hover:bg-purple-50"
                    >
                      {regenerateMutation.isPending ? 'Regenerating...' : 'Regenerate'}
                    </Button>
                    <Button
                      onClick={() => approveMutation.mutate()}
                      disabled={approveMutation.isPending}
                      variant="outline"
                      size="sm"
                      className="border-green-200 text-green-700 hover:bg-green-50"
                    >
                      Approve
                    </Button>
                    <Button
                      onClick={() => approveAndSendMutation.mutate()}
                      disabled={approveAndSendMutation.isPending}
                      variant="outline"
                      size="sm"
                      className="border-green-200 text-green-700 hover:bg-green-50"
                    >
                      Approve & Send
                    </Button>
                    <Button
                      onClick={() => rejectMutation.mutate()}
                      disabled={rejectMutation.isPending}
                      variant="outline"
                      size="sm"
                      className="border-red-200 text-red-700 hover:bg-red-50"
                    >
                      Reject
                    </Button>
                  </>
                )}
                {email.response_status === 'approved' && (
                  <Button
                    onClick={() => sendMutation.mutate()}
                    disabled={sendMutation.isPending}
                    variant="outline"
                    size="sm"
                  >
                    Send Email
                  </Button>
                )}
                {email.response_status === 'rejected' && (
                  <Button
                    onClick={() => reprocessMutation.mutate()}
                    disabled={reprocessMutation.isPending}
                    variant="outline"
                    size="sm"
                    className="border-blue-200 text-blue-700 hover:bg-blue-50"
                  >
                    Reprocess
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary">
                {capitalize(email.category || 'N/A')}
              </Badge>
              <Badge variant="outline">
                {capitalize(email.response_status || 'pending')}
              </Badge>
              {email.extracted_entities?.order_id && (
                <Badge variant="outline" className="break-all">
                  Order: {email.extracted_entities.order_id}
                </Badge>
              )}
              <span className="text-sm text-muted-foreground">
                {new Date(email.received_at || email.created_at).toLocaleString()}
              </span>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Conversation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Conversation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-muted/30 rounded-lg p-4 sm:p-6 border overflow-x-auto">
            {/* Show thread messages if available */}
            {email.thread_messages && email.thread_messages.length > 1 ? (
              <div className="space-y-4">
                {email.thread_messages.map((message: any, index: number) => (
                  <div key={index} className={`p-3 rounded-lg ${index === email.thread_messages.length - 1 ? 'bg-blue-50 border-l-4 border-blue-400' : 'bg-gray-50 border-l-4 border-gray-300'}`}>
                    <div className="text-xs text-gray-500 mb-2">
                      <strong>From:</strong> {message.from_address} 
                      {message.date && <span className="ml-2"><strong>Date:</strong> {new Date(message.date).toLocaleString()}</span>}
                      {index === email.thread_messages.length - 1 && <span className="ml-2 text-blue-600 font-semibold">(Latest)</span>}
                    </div>
                    <div 
                      className="text-sm whitespace-pre-wrap leading-relaxed break-words"
                      dangerouslySetInnerHTML={{ __html: linkifyText(message.body || message.snippet || '') }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              // Fallback to single email body with collapsible view
              (() => {
                const { orderId, productLine, customerMessage, fullBody } = parseEmailBody(email.body);
                
                return (
                  <div className="space-y-4">
                    {/* Always show: Order ID, Product, Customer Message */}
                    {orderId && (
                      <div className="text-sm font-semibold text-blue-600">
                        {orderId}
                      </div>
                    )}
                    {productLine && (
                      <div className="text-sm text-gray-700">
                        {productLine}
                      </div>
                    )}
                    {customerMessage && (
                      <div className="border border-gray-200 rounded-lg p-4 bg-white">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Customer Message</p>
                        <div 
                          className="text-sm whitespace-pre-wrap leading-relaxed break-words text-gray-900"
                          dangerouslySetInnerHTML={{ __html: linkifyText(customerMessage) }}
                        />
                      </div>
                    )}
                    
                    {/* Show More / Show Less button */}
                    <button
                      onClick={() => setShowFullEmail(!showFullEmail)}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                    >
                      {showFullEmail ? (
                        <>
                          <ChevronUp className="h-4 w-4" />
                          Show Less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4" />
                          Show More Details
                        </>
                      )}
                    </button>
                    
                    {/* Full email body (collapsible) */}
                    {showFullEmail && (
                      <div className="border-t pt-4">
                        <p className="text-xs font-semibold text-gray-500 mb-2">Full Email Content:</p>
                        <div 
                          className="text-sm whitespace-pre-wrap leading-relaxed break-words bg-gray-50 p-4 rounded"
                          dangerouslySetInnerHTML={{ __html: linkifyText(fullBody) }}
                        />
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </div>
        </CardContent>
      </Card>

      {/* AI Generated Response */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">Generated Response</CardTitle>
            {!isEditing && email.response_status === 'pending_review' && (
              <Button
                onClick={handleStartEdit}
                variant="outline"
                size="sm"
              >
                Edit Response
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Show version carousel if multiple versions exist */}
          {responseVersions && responseVersions.total_versions > 1 ? (
            <ResponseVersionCarousel
              versions={responseVersions.versions}
              currentVersion={email.response_version || 1}
            />
          ) : (
            <>
              {isEditing ? (
                <div className="space-y-4">
                  <textarea
                    value={editedResponse}
                    onChange={(e) => setEditedResponse(e.target.value)}
                    rows={16}
                    className="w-full px-4 py-3 text-sm border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-input bg-background resize-none"
                    placeholder="Edit the response..."
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveEdit}
                      disabled={updateMutation.isPending}
                      size="sm"
                      variant="outline"
                      className="border-green-200 text-green-700 hover:bg-green-50"
                    >
                      Save Changes
                    </Button>
                    <Button
                      onClick={() => setIsEditing(false)}
                      variant="outline"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-muted/30 rounded-lg p-4 sm:p-6 border overflow-x-auto">
                  <div 
                    className="text-sm leading-relaxed break-words [&>p]:mb-3 [&>ul]:mb-3 [&>ul]:ml-4 [&>ul]:list-disc [&>ol]:mb-3 [&>ol]:ml-4 [&>ol]:list-decimal [&>strong]:font-semibold [&>em]:italic [&>h1]:text-lg [&>h1]:font-bold [&>h1]:mb-2 [&>h2]:text-base [&>h2]:font-bold [&>h2]:mb-2 [&>h3]:text-sm [&>h3]:font-bold [&>h3]:mb-2"
                    dangerouslySetInnerHTML={{ 
                      __html: marked.parse(email.edited_response || email.response_text || 'No response generated') as string
                }}
              />
              
              {/* Feedback buttons for response quality */}
              <div className="flex justify-end mt-4 pt-3 border-t border-gray-200">
                <FeedbackButtons 
                  emailId={email.id} 
                  size="sm" 
                />
              </div>
            </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Comment Section */}
      {email.response_text && (
        <CommentSection emailId={email.id} />
      )}
    </div>
  );
}
